import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../../lib/api';

/**
 * Telephony journey (docs/dev/api-dialer.md „Telephony journey (M8)", Einstellungen 25/26). Every mutation answers
 * the fresh journey; realtime `telephony.updated` refreshes the root `['telephony']`.
 */

export interface ActionState {
  allowed: boolean;
  reason: string | null;
}

export type StepKey = 'account' | 'credentials' | 'bundle' | 'number' | 'setup' | 'test';
export type NumberPurpose = 'caller_id' | 'sms' | 'inbound';

export interface JourneyStep {
  key: StepKey;
  number: number;
  title: string;
  text: string;
  status: 'done' | 'active' | 'locked';
  doneBy: string | null;
  meta: string | null;
  actions: Record<string, ActionState>;
}

export interface JourneyNumber {
  id: string;
  e164: string;
  display: string;
  type: 'mobile' | 'landline';
  capabilities: string[];
  capabilitiesLabel: string;
  purpose: NumberPurpose;
  purposeLabel: string;
  assignedUser: { id: string; name: string } | null;
  poolLabel: string | null;
  active: boolean;
}

export interface Journey {
  step: number;
  done: boolean;
  progressPct: number;
  badge: string;
  steps: JourneyStep[];
  account: { sidMasked: string; friendlyName: string | null; createdOn: string | null } | null;
  credentials: { verifiedAt: string } | null;
  bundle: {
    sidMasked: string;
    status: 'open' | 'waiting' | 'rejected' | 'approved';
    statusLabel: string;
    submittedAt: string | null;
    lastCheckedAt: string | null;
    rejection: { reason: string; explanation: string; previous: boolean } | null;
    documents: { friendlyName: string; type: string; status: string }[];
    consoleUrl: string;
  } | null;
  companyName: string;
  search: { enabled: boolean; lockedReason: string | null };
  numbers: JourneyNumber[];
  purchaseEnabled: boolean;
  purchaseReason: string | null;
  testCallAt: string | null;
  testMessageAt: string | null;
}

export interface NumberSearchResult {
  e164: string;
  display: string;
  locality: string | null;
  type: 'local' | 'mobile';
  typeLabel: string;
  capabilities: string[];
  capabilitiesLabel: string;
  priceLabel: string | null;
}

export interface NumberSetupEntry {
  phoneNumberId: string;
  purpose: NumberPurpose;
  assignedUserId: string | null;
  poolLabel: string | null;
}

const BASE = '/settings/organization/telephony';
export const journeyKey = ['telephony', 'journey'] as const;

export function useJourney() {
  return useQuery({
    queryKey: journeyKey,
    queryFn: () => api<Journey>(BASE),
    retry: false,
  });
}

function useJourneyMutation<TInput>(request: (input: TInput) => Promise<Journey>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: (journey) => queryClient.setQueryData(journeyKey, journey),
  });
}

export function useSaveAccount() {
  return useJourneyMutation((accountSid: string) =>
    api<Journey>(`${BASE}/account`, { method: 'PUT', body: { accountSid } }),
  );
}

export function useSaveCredentials() {
  return useJourneyMutation((authToken: string) =>
    api<Journey>(`${BASE}/credentials`, { method: 'PUT', body: { authToken } }),
  );
}

export function useSaveBundle() {
  return useJourneyMutation((bundleSid: string) =>
    api<Journey>(`${BASE}/bundle`, { method: 'PUT', body: { bundleSid } }),
  );
}

export function useCheckBundle() {
  return useJourneyMutation(() => api<Journey>(`${BASE}/bundle/check`, { method: 'POST' }));
}

export function searchNumbers(type: 'local' | 'mobile', areaCode: string) {
  const params = new URLSearchParams({ type });
  if (areaCode.trim() !== '') params.set('areaCode', areaCode.trim());
  return api<{ results: NumberSearchResult[]; costNote: string }>(
    `${BASE}/numbers/search?${params.toString()}`,
  );
}

export function usePurchaseNumber() {
  return useJourneyMutation(async (e164: string) => {
    const result = await api<{ phoneNumberId: string; journey: Journey }>(`${BASE}/numbers/purchase`, {
      method: 'POST',
      body: { e164 },
    });
    return result.journey;
  });
}

export function useSetupNumbers() {
  return useJourneyMutation((numbers: NumberSetupEntry[]) =>
    api<Journey>(`${BASE}/setup`, { method: 'POST', body: { numbers } }),
  );
}

export function useTestCall() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (to: string) =>
      api<{ to: string; journey: Journey }>(`${BASE}/test-call`, {
        method: 'POST',
        body: to.trim() === '' ? {} : { to: to.trim() },
      }),
    onSuccess: (result) => queryClient.setQueryData(journeyKey, result.journey),
  });
}

export function useTestMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (to: string) =>
      api<{ to: string; journey: Journey }>(`${BASE}/test-message`, {
        method: 'POST',
        body: { to: to.trim() },
      }),
    onSuccess: (result) => queryClient.setQueryData(journeyKey, result.journey),
  });
}

/** „Coaching-Gruppe (WhatsApp-Chat-ID)" (docs/dev/api-dialer.md „AI"): the group of the post-call coaching. */
export interface CoachingSettings {
  chatId: string | null;
  /** False while the coaching bridge is not set up on the server: the feedback is stored, not posted. */
  bridgeConfigured: boolean;
}

export const coachingKey = ['telephony', 'coaching'] as const;

export function useCoachingSettings() {
  return useQuery({
    queryKey: coachingKey,
    queryFn: () => api<CoachingSettings>(`${BASE}/coaching`),
  });
}

export function useSaveCoachingSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (chatId: string) =>
      api<CoachingSettings>(`${BASE}/coaching`, { method: 'PUT', body: { chatId: chatId.trim() || null } }),
    onSuccess: (data) => queryClient.setQueryData(coachingKey, data),
  });
}
